/** @typedef {'google' | 'local' | undefined} GitbaseAuthMode */

/** @type {{ apiKeys: Record<string, string>, deployHookURL?: string, deployHookAuthHeader?: string, authMode: GitbaseAuthMode }} */
const sessionSecrets = {
  apiKeys: {},
  deployHookURL: undefined,
  deployHookAuthHeader: undefined,
  authMode: undefined,
};

/**
 * @param {Partial<typeof sessionSecrets>} data
 */
export const setGitbaseSessionSecrets = (data) => {
  if (data.apiKeys) {
    sessionSecrets.apiKeys = { ...sessionSecrets.apiKeys, ...data.apiKeys };
  }

  if (data.deployHookURL !== undefined) {
    sessionSecrets.deployHookURL = data.deployHookURL;
  }

  if (data.deployHookAuthHeader !== undefined) {
    sessionSecrets.deployHookAuthHeader = data.deployHookAuthHeader;
  }

  if (data.authMode !== undefined) {
    sessionSecrets.authMode = data.authMode;
  }
};

export const getGitbaseSessionSecrets = () => sessionSecrets;

/**
 * @param {string} [serviceId]
 * @returns {string | undefined}
 */
export const getGitbaseApiKey = (serviceId) => sessionSecrets.apiKeys[serviceId];

export const getGitbaseDeployHookURL = () => sessionSecrets.deployHookURL;

export const getGitbaseDeployHookAuthHeader = () => sessionSecrets.deployHookAuthHeader;

/**
 * @param {GitbaseAuthMode} authMode
 */
export const setGitbaseAuthMode = (authMode) => {
  sessionSecrets.authMode = authMode;
};

export const getGitbaseAuthMode = () => sessionSecrets.authMode;

/** @returns {boolean} */
export const shouldGitbaseSkipCI = () => sessionSecrets.authMode === 'google';

/** Strip GitBase-managed secrets before persisting prefs to localStorage. */
export const stripGitbasePrefsSecrets = (/** @type {Record<string, unknown>} */ snapshot) => {
  const next = { ...snapshot };

  if (next.apiKeys && typeof next.apiKeys === 'object') {
    const { cloudflare_r2, pexels, pixabay, unsplash, ...rest } = /** @type {Record<string, string>} */ (
      next.apiKeys
    );
    void cloudflare_r2;
    void pexels;
    void pixabay;
    void unsplash;
    next.apiKeys = rest;
  }

  delete next.deployHookURL;
  delete next.deployHookAuthHeader;

  return next;
};

/** Merge session secrets into prefs for runtime consumers. */
export const mergeGitbasePrefsSecrets = (/** @type {Record<string, unknown>} */ snapshot) => {
  const next = { ...snapshot };

  if (Object.keys(sessionSecrets.apiKeys).length) {
    next.apiKeys = {
      ...(typeof next.apiKeys === 'object' ? next.apiKeys : {}),
      ...sessionSecrets.apiKeys,
    };
  }

  if (sessionSecrets.deployHookURL) {
    next.deployHookURL = sessionSecrets.deployHookURL;
  }

  if (sessionSecrets.deployHookAuthHeader) {
    next.deployHookAuthHeader = sessionSecrets.deployHookAuthHeader;
  }

  return next;
};
